import { QUERY_KEYS } from "@repo/baseCloud/src/api/queryKeys";
import { holdPropsInUtc } from "@repo/helpers";
import { FilterItem, keyPair, MvcBranch, MvcLocation } from "@repo/models";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

import { useLocationContext } from "../contexts/editableItemContext";
import { LocationStatus } from "../models";
import { useLocationPortalApi, useQueryWrapper } from "./locationPortalApi";
import * as Queries from "./queries";

export function useLocationApi() {
	const { Get, Post } = useLocationPortalApi();
	const queryClient = useQueryClient();

	const initializeLocation = useCallback((location: MvcLocation) => {
		location.stateId = location.stateId || null;
		return location;
	}, []);

	const saveLocation = useCallback(
		async function (location: MvcLocation) {
			const toSave = {
				...location,
				detailsDict: Object.fromEntries(
					Object.entries(location.details ?? {}).map(([key, value]) => {
						return [`${key.charAt(0).toUpperCase()}${key.slice(1)}`, value];
					}),
				),
			};

			const locationId = await Post<number, MvcLocation>("SaveLocation", null, toSave);
			await Promise.all([
				queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.Locations.Locations] }),
				queryClient.invalidateQueries({ queryKey: [...Queries.LOCATION.DETAILS, locationId], exact: true }),
			]);
			return locationId;
		},
		[Post, queryClient],
	);

	const updateLocationStatus = useCallback(
		function (locationId: number, status: Partial<LocationStatus>) {
			return Post<string>("UpdateLocationStatus", null, { locationId, status });
		},
		[Post],
	);

	const loadLocation = useCallback(
		function (id: number) {
			return Get<MvcLocation>("GetLocation", { id }).then(initializeLocation);
		},
		[Get, initializeLocation],
	);

	const getLocations = useCallback(
		async function () {
			return await Get<keyPair[]>("GetLocations");
		},
		[Get],
	);

	const getAllLocations = useCallback(
		async function () {
			const locations = await Get<MvcLocation[]>("GetAllLocations");
			return locations.map(initializeLocation);
		},
		[Get, initializeLocation],
	);

	const getStatusHistories = useCallback(
		async function (locationId: number) {
			const histories = await Get<LocationStatus[]>("GetStatusHistories", { locationId });
			for (let history of histories) holdPropsInUtc(history, "startDate", "endDate");
			return histories;
		},
		[Get, initializeLocation],
	);

	const getBrands = useCallback(() => Get<FilterItem[]>("GetBrands"), [Get]);
	const getChannels = useCallback(() => Get<FilterItem[]>("GetChannels"), [Get]);
	const getCompanyDivisions = useCallback(() => Get<FilterItem[]>("GetCompanyDivisions"), [Get]);
	const getReps = useCallback(() => Get<FilterItem[]>("GetReps"), [Get]);
	const getCountries = useCallback(() => Get<FilterItem[]>("GetCountries"), [Get]);
	const getStatesByCountry = useCallback(() => Get<StateWithCountry[]>("GetStates"), [Get]);
	const getRetailPartnerTypes = useCallback(() => Get<FilterItem[]>("GetRetailPartnerTypes"), [Get]);
	const getRetailPartners = useCallback(() => Get<RetailPartner[]>("GetRetailPartners"), [Get]);
	const getLocationStatuses = useCallback(() => Get<FilterItem[]>("GetLocationStatuses"), [Get]);

	const getAllBranches = useCallback(() => Get<MvcBranch[]>("GetAllBranches"), [Get]);

	return {
		saveLocation,
		loadLocation,
		getLocations,
		getAllLocations,
		getBrands,
		getChannels,
		getCompanyDivisions,
		getReps,
		getCountries,
		getStatesByCountry,
		getRetailPartnerTypes,
		getRetailPartners,
		getStatusHistories,
		updateLocationStatus,
		getLocationStatuses,
		getAllBranches,
	};
}

export function useLocationQueries() {
	const { loadLocation, getStatusHistories } = useLocationApi();
	const { locationId } = useLocationContext();

	const useLocationInfoQuery = (setLocationId?: number) =>
		useQueryWrapper(
			Queries.LOCATION.DETAILS,
			[setLocationId ?? locationId],
			() => loadLocation(setLocationId ?? locationId),
			{
				staleTime: Infinity,
				enabled: !!setLocationId || !!locationId,
			},
		);

	const useStatusHistoriesQuery = (locationId: number) =>
		useQueryWrapper(Queries.LOCATION.STATUS_HISTORIES, [locationId], () => getStatusHistories(locationId), {
			enabled: !!locationId,
		});

	return {
		useLocationInfoQuery,
		useStatusHistoriesQuery,
	};
}

export interface RetailPartner extends FilterItem {
	typeId: number;
	code: string;
}
interface StateWithCountry {
	usStateId: number;
	name: string;
	countryId: number;
}
